/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {batch} from 'react-redux';
import {error} from '@ion/core/src/logger/logger';
import {
  acknowledgeAlerts,
  getAlertDetails,
  getAlertsFired
} from '@ion/core/src/services/Alerts';
import {config} from '@ion/core/src/services/ConnectUtils';
import {toNotificationsAccount} from '@ion/core/src/services/FetchWorkerUtils';
import {getUrgentNotificationsEnabled} from '@ion/core/src/services/GroupProperties';
import NotificationsWorker from '@ion/core/src/workers/NotificationsWorker';
import {Notification} from '@ion/dls/src/types/extensions';
import {NotificationPanelTabs} from '@ion/dls/src/types/types';
import Features, {hasAppNotificationAccess} from '@/security/Features';
import {getAllAccountIds} from '@/store/helpers';
import {PanelState} from '@/store/reducers/notifications';

export const SET_NOTIFICATIONS_PANEL_OPEN       = 'SET_NOTIFICATIONS_PANEL_OPEN';
export const SET_NOTIFICATIONS_ACTIVE_PANEL     = 'SET_NOTIFICATIONS_ACTIVE_PANEL';
export const SET_NOTIFICATION_PANEL_STATE       = 'SET_NOTIFICATION_PANEL_STATE';
export const REMOVE_TOAST_NOTIFICATION          = 'REMOVE_TOAST_NOTIFICATION';
export const REMOVE_URGENT_ENABLED_NOTIFICATION = 'REMOVE_URGENT_ENABLED_NOTIFICATION';

export const GET_NOTIFICATION_DETAILS_REQUEST   = 'GET_NOTIFICATION_DETAILS_REQUEST';
export const GET_NOTIFICATION_DETAILS_RESPONSE  = 'GET_NOTIFICATION_DETAILS_RESPONSE';
export const GET_NOTIFICATION_DETAILS_ERROR     = 'GET_NOTIFICATION_DETAILS_ERROR';

export const MARK_AS_READ_REQUEST               = 'MARK_AS_READ_REQUEST';
export const MARK_AS_READ_RESPONSE              = 'MARK_AS_READ_RESPONSE';
export const MARK_AS_READ_ERROR                 = 'MARK_AS_READ_ERROR';

export const MARK_AS_CLEARED_REQUEST            = 'MARK_AS_CLEARED_REQUEST';
export const MARK_AS_CLEARED_RESPONSE           = 'MARK_AS_CLEARED_RESPONSE';
export const MARK_AS_CLEARED_ERROR              = 'MARK_AS_CLEARED_ERROR';

export const ALERTS_REFRESH_ACTIVATED           = 'ALERTS_REFRESH_ACTIVATED';
export const ALERTS_REFRESH_UPDATED             = 'ALERTS_REFRESH_UPDATED';
export const ALERTS_REFRESH_DEACTIVATED         = 'ALERTS_REFRESH_DEACTIVATED';

export const SELECT_NOTIFICATION                = 'SELECT_NOTIFICATION';
export const SELECT_ALL_NOTIFICATIONS           = 'SELECT_ALL_NOTIFICATIONS';
export const DESELECT_ALL_NOTIFICATIONS         = 'DESELECT_ALL_NOTIFICATIONS';

// reset notification state
export const RESET_NOTIFICATION_REQUEST         = 'RESET_NOTIFICATION_REQUEST';
export const RESET_NOTIFICATION_RESPONSE        = 'RESET_NOTIFICATION_RESPONSE';
export const RESET_NOTIFICATION_ERROR           = 'RESET_NOTIFICATION_ERROR';

// update Notification state
export const GET_NOTIFICATION_REQUEST           = 'GET_NOTIFICATION_REQUEST';
export const GET_NOTIFICATION_REPONSE           = 'GET_NOTIFICATION_REPONSE';
export const GET_NOTIFICATION_ERROR             = 'GET_NOTIFICATION_ERROR';

/**
 * @setNotificationsPanelStatus
 */
export const setNotificationsPanelStatus = (state: boolean) => ({
  type : SET_NOTIFICATIONS_PANEL_OPEN, open : state
});

/**
 * @function setNotificationPanelState
 */
export const setNotificationPanelState = (newState: PanelState[]) => {
  return {type: SET_NOTIFICATION_PANEL_STATE, newState};
};

/**
 * @setNotificationsActivePanel
 */
export const setNotificationsActivePanel = (activePanel: NotificationPanelTabs) => ({
  type : SET_NOTIFICATIONS_ACTIVE_PANEL, activePanel
});

/**
 * @removeToastNotification
 */
export const removeToastNotification = (index: number) => ({
  type : REMOVE_TOAST_NOTIFICATION, index
});

/**
 * @removeUrgentEnabledNotification
 */
export const removeUrgentEnabledNotification = (uuid: string) => ({
  type : REMOVE_URGENT_ENABLED_NOTIFICATION, uuid
});

/**
 * Defines a utility to get Notifications based on the account hierarchy
 */
export const getNotifications = (omitRequest: boolean = false) => {
  return async(dispatch, getState) => {
    !omitRequest && dispatch({type: GET_NOTIFICATION_REQUEST});
    if (!hasAppNotificationAccess()) {
      dispatch({type: GET_NOTIFICATION_REPONSE, notifications: []});
      return;
    }
    try {
      let notifications;
      if (!Features.isCurrentAccountRestricted()) {
        notifications = await getAlertsFired({accountIds: getAllAccountIds(getState)});
      }
      dispatch({type: GET_NOTIFICATION_REPONSE, notifications});
    } catch (err) {
      error(err);
      dispatch({type: GET_NOTIFICATION_ERROR});
    }
  };
};

/**
 * @getNotificationDetails
 */
export const getNotificationDetails = (notification: Notification) => {
  return async dispatch => {
    dispatch({type: GET_NOTIFICATION_DETAILS_REQUEST});
    try {
      const details = await getAlertDetails(notification);
      dispatch({type: GET_NOTIFICATION_DETAILS_RESPONSE, notification: {...notification, details}});
    } catch (err) {
      error(err);
      dispatch({type: GET_NOTIFICATION_DETAILS_ERROR});
    }
  };
};

/**
 * Provides the action which facilitates marking an individual notification as read.
 */
export const markAsRead = (notification: Notification) => {
  return async dispatch => {
    dispatch(markAllAsRead([notification]));
  };
};

/**
 * Provides the action which facilitates marking a collection of notifications as read.
 * This action corresponds to the "Mark All as Read" and "Mark Selected as Read"
 * Notification Features.
 *
 * @see https://www.figma.com/file/6qLooOCm1rBKMPF0F6X5tU/02_Design-Sprint-2-(Tracking%2C-Notifications)?node-id=5011%3A104994
 */
export const markAllAsRead = (notifications: Notification[]) => {
  return async dispatch => {
    dispatch({type: MARK_AS_READ_REQUEST, notifications});
    try {
      await acknowledgeAlerts(notifications.map(({id}) => id) as number[]);
      dispatch({type: MARK_AS_READ_RESPONSE, notifications});
      dispatch(deselectAllNotifications());
    } catch (err) {
      error(err);
      dispatch({type: MARK_AS_READ_ERROR});
    }
  };
};

/**
 * Provides the action which facilitates marking an individual notification as cleared.
 */
export const markAsCleared = (notification: Notification) => {
  return async dispatch => {
    dispatch(markAllAsCleared([notification]));
  };
};

/**
 * Provides the action which facilitates marking a collection of notifications as cleared.
 * This action corresponds to the "Mark All as Cleared" and "Mark Selected as Cleared"
 * Notification Features.
 *
 * @see https://www.figma.com/file/6qLooOCm1rBKMPF0F6X5tU/02_Design-Sprint-2-(Tracking%2C-Notifications)?node-id=5011%3A104994
 */
export const markAllAsCleared = (notifications: Notification[]) => {
  return async dispatch => {
    dispatch({type: MARK_AS_CLEARED_REQUEST, notifications});
    try {
      // [ION-APP TODO]: Add updated call once clearAlerts service API has been implemented
      // Currently, this action only allows for clearing an alert during the current session.
      // const updated = await clearAlerts(notifications.map(({id}) => id));
      dispatch({type: MARK_AS_CLEARED_RESPONSE, notifications});
      dispatch(deselectAllNotifications());
    } catch (err) {
      error(err);
      dispatch({type: MARK_AS_CLEARED_ERROR});
    }
  };
};

/**
 * @selectNotification
 *
 * Adds or removes a notification from an array that
 * tracks which notifications have been selected by the checkboxes
 * for performing bulk actions on the list of selected items
 *
 */
export const selectNotification = (checked: boolean, selectedNotification: Notification) => {
  return (dispatch: Function, getState: Function) => {
    const {
      notifications: {
        panel: {selected}
      }
    } = getState();
    const updatedSelected = [...selected];
    const matchingIndex = updatedSelected.findIndex(item =>
      item?.uuid === selectedNotification?.uuid
    );
    if (matchingIndex > -1) {
      if (checked) updatedSelected.splice(matchingIndex, 1);
    } else {
      updatedSelected.push(selectedNotification);
    }
    dispatch({type: SELECT_NOTIFICATION, selectedNotifications: updatedSelected});
  };
};

/**
 * @selectAllNotifications
 *
 * Selects all notifications for the active tab
 * multi-select
 */
export const selectAllNotifications = () => {
  return (dispatch: Function, getState: Function) => {
    const {
      notifications: {
        panel: {activePanel},
        all,
        urgent
      }
    } = getState();
    dispatch({
      type          : SELECT_ALL_NOTIFICATIONS,
      notifications : {
        all    : activePanel ? all : [],
        urgent : !activePanel ? urgent : []
      }
    });
  };
};

/**
 * Deselects all notifications for the active tab multi-select
 */
export const deselectAllNotifications = () => {
  return (dispatch: Function, getState: Function) => {
    const {
      notifications: {
        panel: {activePanel},
        all,
        urgent
      }
    } = getState();
    dispatch({
      type          : DESELECT_ALL_NOTIFICATIONS,
      notifications : {
        all    : activePanel ? all : [],
        urgent : !activePanel ? urgent : []
      }
    });
  };
};

/**
 * Builds the NotificationsWorker update handler. Kept outside of the thunk so
 * the closure retains only `dispatch`, and never the globalGroup state.
 */
const createRefreshHandler = (dispatch: Function) => (notifications: Notification[]) => {
  dispatch({
    type                       : ALERTS_REFRESH_UPDATED,
    latest                     : notifications,
    urgentNotificationsEnabled : getUrgentNotificationsEnabled()
  });
};

/**
 * Invoked post application initialization after the initial notifications have
 * been retrieved.
 */
export const activateRefresh = () => {
  return async(dispatch, getState) => {
    if (hasAppNotificationAccess()) {
      dispatch({type: ALERTS_REFRESH_ACTIVATED});
      NotificationsWorker.start({
        account  : toNotificationsAccount(getState().globalGroup),
        onUpdate : createRefreshHandler(dispatch),
        config
      });
    } else {
      NotificationsWorker.destruct();
    }
  };
};

/**
 * When invoked, the automated notifications refresh will be deactivated.
 */
export const deactivateRefresh = () => {
  return async() => {
    NotificationsWorker.destruct();
  };
};

// invoked when the global group has been changed/updated.
export const resetNotifications = () => {
  return async(dispatch, getState) => {
    if (!hasAppNotificationAccess()) {
      NotificationsWorker.destruct();
      dispatch({type: RESET_NOTIFICATION_RESPONSE, notifications: []});
      return;
    }
    const {globalGroup} = getState();
    if (!Features.isCurrentAccountRestricted()) {
      try {
        batch(() => {
          dispatch({type: RESET_NOTIFICATION_REQUEST});
          dispatch(getNotifications(true));
        });
        NotificationsWorker.onAccountChange(toNotificationsAccount(globalGroup));
      } catch (err) {
        error(err);
        dispatch({type: RESET_NOTIFICATION_ERROR});
      }
    }
  };
};
